import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import amazonLogo from "@/assets/logos/amazon.avif?w=240&format=webp&quality=80";
import cityOfTorontoLogo from "@/assets/logos/city-of-toronto.avif?w=240&format=webp&quality=80";
import dhlLogo from "@/assets/logos/dhl.avif?w=240&format=webp&quality=80";
import deloitteLogo from "@/assets/logos/deloitte.avif?w=240&format=webp&quality=80";
import jllLogo from "@/assets/logos/jll.png?w=240&format=webp&quality=80";
import cbreLogo from "@/assets/logos/cbre.png?w=240&format=webp&quality=80";
import fastfrateLogo from "@/assets/logos/fastfrate.png?w=240&format=webp&quality=80";
import cocaColaLogo from "@/assets/logos/coca-cola.webp?w=240&format=webp&quality=80";
import winnersLogo from "@/assets/logos/winners.avif?w=240&format=webp&quality=80";
import russelMetalsLogo from "@/assets/logos/russel-metals.png?w=240&format=webp&quality=80";
import cevaLogisticsLogo from "@/assets/logos/ceva-logistics.png?w=240&format=webp&quality=80";

// Shared logos (both regions)
const sharedLogos = [
  { name: "Amazon", logo: amazonLogo },
  { name: "DHL", logo: dhlLogo },
  { name: "Coca-Cola", logo: cocaColaLogo },
  { name: "Deloitte", logo: deloitteLogo },
  { name: "JLL", logo: jllLogo },
  { name: "CBRE", logo: cbreLogo },
  { name: "Fastfrate", logo: fastfrateLogo },
];

// Canada-only logos
const canadaLogos = [
  { name: "City of Toronto", logo: cityOfTorontoLogo },
  { name: "Winners", logo: winnersLogo },
];

// USA-only logos
const usaLogos = [
  { name: "Russel Metals", logo: russelMetalsLogo },
  { name: "CEVA Logistics", logo: cevaLogisticsLogo },
];

const HeroSection = () => {
  const { isUSA } = useI18n();
  const logos = [...sharedLogos, ...(isUSA ? usaLogos : canadaLogos)];
  const fullText = "Engineering, Building & Supporting Industrial & Commercial Infrastructure";
  const isPrerender = typeof window !== "undefined" && (window as Window & { __MTLI_PRERENDER?: boolean }).__MTLI_PRERENDER === true;
  const [isMobile, setIsMobile] = useState(true);
  const [mounted, setMounted] = useState(false);
  const skipAnimation = isPrerender || isMobile;
  const [displayedText, setDisplayedText] = useState(skipAnimation ? fullText : "");
  const [isComplete, setIsComplete] = useState(skipAnimation);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  useEffect(() => {
    setMounted(true);
    setIsMobile(window.matchMedia("(max-width: 767px)").matches);
  }, []);

  useEffect(() => {
    if (skipAnimation) return;

    let i = 0;
    const interval = setInterval(() => {
      if (i < fullText.length) {
        setDisplayedText(fullText.slice(0, i + 1));
        i++;
      } else {
        setIsComplete(true);
        clearInterval(interval);
      }
    }, 35);
    return () => clearInterval(interval);
  }, [skipAnimation]);

  useEffect(() => {
    if (isMobile) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let timeoutId: number | undefined;

    const loadVideo = () => setShouldLoadVideo(true);
    const schedule = () => {
      if (idleWindow.requestIdleCallback) {
        idleId = idleWindow.requestIdleCallback(loadVideo, { timeout: 3500 });
      } else {
        timeoutId = window.setTimeout(loadVideo, 2500);
      }
    };

    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });

    return () => {
      window.removeEventListener("load", schedule);
      if (idleId !== undefined && idleWindow.cancelIdleCallback) idleWindow.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [isMobile]);


  return (
    <section className="relative bg-secondary overflow-hidden">
      {/* Video Background - desktop only, deferred */}
      {mounted && !isMobile && (
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload={shouldLoadVideo ? "metadata" : "none"}
            className="absolute inset-0 w-full h-full object-cover"
          >
            {shouldLoadVideo && <source src="/videos/hero-drone.mp4" type="video/mp4" />}
          </video>
          <div className="absolute inset-0 bg-secondary/85" />
        </div>
      )}


      {/* Main Hero Content */}
      <div className="relative z-10 container mx-auto px-4 py-20 lg:py-32 min-h-[80vh] flex items-center">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            <span className="text-sm font-medium text-white">MTLI Group of Companies</span>
            <span className="w-px h-4 bg-white/30" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-medium">Manufacturing • Technology • Logistics • Infrastructure</span>
          </div>

          {/* H1 with typewriter effect */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight min-h-[3.5em]">
            {displayedText}
            {!isComplete && <span className="inline-block w-[3px] h-[1em] bg-white ml-1 animate-pulse align-baseline" />}
          </h1>


          {/* Subheader */}
          <p className={`text-lg md:text-xl text-white/60 mb-2 max-w-3xl transition-opacity duration-700 font-medium tracking-wide ${isComplete ? 'opacity-100' : 'opacity-0'}`}>
            Racking. Automation. Construction. Electrical. Facility Transitions. Lifecycle Maintenance.
          </p>
          <p className={`text-lg md:text-xl text-white/80 mb-8 max-w-3xl transition-opacity duration-700 ${isComplete ? 'opacity-100' : 'opacity-0'}`}>
            One Integrated Platform. Built to Scale.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <Button size="lg" className="text-lg px-8 bg-white text-primary hover:bg-white/90" asChild>
              <Link to="/book-a-call">
                Book a Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 bg-transparent border-white text-white hover:bg-white hover:text-primary" asChild>
              <Link to="/services">
                Explore Our Capabilities
              </Link>
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap gap-6 items-center bg-black/10 backdrop-blur-sm rounded-xl p-5 w-fit">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-white">15,000+</span>
              <span className="text-sm text-white/70">Projects<br />Completed</span>
            </div>
            <div className="w-px h-10 bg-white/20 hidden sm:block" />
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-white">40+</span>
              <span className="text-sm text-white/70">Years of<br />Experience</span>
            </div>
            <div className="w-px h-10 bg-white/20 hidden sm:block" />
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-white">800+</span>
              <span className="text-sm text-white/70">Vendors &<br />Partners</span>
            </div>
          </div>
        </div>
      </div>

      {/* Logo Banner - Inside Hero */}
      <div className="relative z-10 border-t border-white/20">
        <div className="container mx-auto px-4 py-8">
          <p className="text-center text-white/80 text-sm font-medium mb-6">
            Trusted by industry leaders
          </p>
          
          {/* Logo row */}
          <div className="relative overflow-hidden">
            {/* Gradient masks */}
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-secondary to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-secondary to-transparent z-10" />
            
            <div className="flex gap-8 animate-scroll items-center">
              {[...logos, ...logos].map((item, index) => (
                <div 
                  key={`logo-${item.name}-${index}`}
                  className="flex items-center justify-center h-12 w-[150px] min-w-[150px] px-4 bg-white rounded-lg"
                >
                  <img 
                    src={item.logo} 
                    alt={`${item.name} logo`}
                    className="max-h-[28px] w-auto max-w-[120px] object-contain"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => e.currentTarget.style.display = 'none'}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
